// Embed — BlockNote 커스텀 블록. URL 을 iframe 으로 임베드.
//
// - props.url / height(px, 기본 400).
// - 지원: YouTube(watch/shorts/live/youtu.be → embed URL 변환), Vimeo(player 변환),
//   Figma(file/design/proto/board → /embed 래퍼 변환),
//   Google Maps(output=embed), PDF(.pdf 그대로), 그 외 일반 URL 도 iframe 시도
//   (X-Frame-Options 거부 사이트는 브라우저가 빈 화면 처리 — 사전 판별 불가).
// - iframe sandbox="allow-scripts allow-same-origin allow-popups allow-forms".
//   단, PDF(pathname 이 .pdf) 는 sandbox 생략 — Chromium 이 sandboxed iframe 내
//   내장 PDF 뷰어를 차단할 수 있기 때문.
// - 높이: 프리셋(낮게/보통/높게) 버튼 + 하단 핸들 드래그. 드래그 중엔 투명 커버로
//   iframe 이 pointer 이벤트를 삼키지 않게 한다.
//
// ⚠️ createReactBlockSpec 은 생성 함수 반환 — 끝의 () 호출 필수(2026-06-18 사고, LinkedDatabaseView.jsx).

import { useEffect, useState } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import './custom-blocks.css';

const MIN_H = 120;
const MAX_H = 1200;
const PRESETS = [
  { label: '낮게', h: 240 },
  { label: '보통', h: 400 },
  { label: '높게', h: 640 },
];

function normalizeUrl(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

// 원본 URL → 임베드 가능한 URL 변환. 변환 규칙이 없으면 원본 그대로 iframe 시도.
export function toEmbedUrl(raw) {
  const url = normalizeUrl(raw);
  if (!url) return '';
  let u;
  try { u = new URL(url); } catch { return url; }
  const host = u.hostname.replace(/^www\./i, '').toLowerCase();

  // YouTube — youtu.be/<id>, /watch?v=, /shorts/<id>, /live/<id>, /embed/<id>
  if (host === 'youtu.be') {
    const id = u.pathname.split('/').filter(Boolean)[0];
    if (id) return `https://www.youtube.com/embed/${id}`;
  }
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    const v = u.searchParams.get('v');
    if (u.pathname === '/watch' && v) return `https://www.youtube.com/embed/${v}`;
    const m = u.pathname.match(/^\/(shorts|live|embed)\/([^/?#]+)/);
    if (m) return `https://www.youtube.com/embed/${m[2]}`;
  }

  // Figma — 파일/프로토타입/보드 URL 은 공식 embed 엔드포인트로 변환.
  // (figma.com 원본 URL 은 X-Frame-Options 로 iframe 거부 — /embed 래퍼만 임베드 허용)
  if (host === 'figma.com') {
    if (u.pathname.startsWith('/embed')) return url;
    if (/^\/(file|design|proto|board|slides|deck)\//.test(u.pathname)) {
      return `https://www.figma.com/embed?embed_host=rhoot&url=${encodeURIComponent(url)}`;
    }
  }

  // Vimeo — vimeo.com/<id> → player.vimeo.com/video/<id>
  if (host === 'vimeo.com') {
    const m = u.pathname.match(/\/(\d+)/);
    if (m) return `https://player.vimeo.com/video/${m[1]}`;
  }
  if (host === 'player.vimeo.com') return url;

  // Google Maps — /maps/embed 는 그대로, 일반 지도 URL 은 output=embed 부착
  if ((host === 'google.com' || host.endsWith('.google.com')) && u.pathname.startsWith('/maps')) {
    if (u.pathname.startsWith('/maps/embed')) return url;
    u.searchParams.set('output', 'embed');
    return u.toString();
  }
  if (host === 'maps.google.com') {
    u.searchParams.set('output', 'embed');
    return u.toString();
  }

  // PDF — 브라우저 내장 뷰어가 iframe 안에서 그대로 열린다.
  // 그 외 일반 URL 도 그대로 iframe 시도.
  return url;
}

// PDF 여부 판별 — pathname 기준(.pdf), 쿼리스트링은 무시.
// Chromium 은 sandbox 속성이 있는 iframe 안에서 내장 PDF 뷰어(플러그인)를 차단할 수 있어
// PDF 는 sandbox 를 생략한다 (내장 뷰어는 자체적으로 격리됨).
export function isPdfUrl(raw) {
  try {
    return /\.pdf$/i.test(new URL(normalizeUrl(raw)).pathname);
  } catch {
    return false;
  }
}

// ── 자체 스토리지 HTML 임베드 판별 (2026-07-06) ──
// Supabase Storage 는 기본 도메인(supabase.co)에서 HTML 파일을 XSS/피싱 방지 목적으로
// text/plain 으로 강제 서빙한다 → iframe src 로 걸면 렌더링 대신 소스 텍스트가 보이고,
// charset 부재로 한글도 깨진다 (노션 ZIP 가져오기 HTML 첨부 사고).
// → 우리 스토리지의 .html/.htm 은 fetch 로 내용을 받아 <iframe srcDoc> 으로 렌더한다.
//   sandbox 에 allow-same-origin 을 절대 넣지 않는다 — srcdoc 은 기본이 부모 origin 이라
//   허용 시 첨부 HTML 의 스크립트가 앱 세션/스토리지에 접근 가능(opaque origin 으로 격리).
const STORAGE_PUBLIC_PREFIX = `${process.env.REACT_APP_SUPABASE_URL || ''}/storage/v1/object/public/`;
export function isOwnStorageHtml(raw) {
  if (!process.env.REACT_APP_SUPABASE_URL) return false;
  const url = normalizeUrl(raw);
  if (!url.startsWith(STORAGE_PUBLIC_PREFIX)) return false;
  try {
    return /\.html?$/i.test(new URL(url).pathname);
  } catch {
    return false;
  }
}

function EmbedView({ block, editor }) {
  const editable = editor.isEditable;
  const { url } = block.props;
  const height = Math.max(MIN_H, Math.min(MAX_H, Number(block.props.height) || 400));
  const [urlDraft, setUrlDraft] = useState(url || '');
  const [editingUrl, setEditingUrl] = useState(false);
  const [dragH, setDragH] = useState(null); // 드래그 중 임시 높이(px)

  useEffect(() => { setUrlDraft(url || ''); }, [url]);

  // 자체 스토리지 HTML → srcdoc 렌더 (위 isOwnStorageHtml 주석 참조).
  // 훅 규칙: 아래 조기 return 보다 먼저 선언되어야 한다.
  const src = toEmbedUrl(url);
  const srcdocMode = isOwnStorageHtml(src);
  const [srcdocHtml, setSrcdocHtml] = useState(null); // null=로딩 중
  const [srcdocFailed, setSrcdocFailed] = useState(false);
  useEffect(() => {
    if (!srcdocMode) return undefined;
    let alive = true;
    setSrcdocHtml(null);
    setSrcdocFailed(false);
    fetch(src)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      // Response.text() 는 항상 UTF-8 로 디코드 — text/plain 서빙의 charset 깨짐도 함께 해소
      .then((t) => { if (alive) setSrcdocHtml(t); })
      .catch(() => { if (alive) setSrcdocFailed(true); });
    return () => { alive = false; };
  }, [src, srcdocMode]);

  const submitUrl = () => {
    const next = normalizeUrl(urlDraft);
    if (!next) return;
    editor.updateBlock(block, { props: { url: next } });
    setEditingUrl(false);
  };

  const setHeight = (h) => {
    editor.updateBlock(block, { props: { height: Math.max(MIN_H, Math.min(MAX_H, Math.round(h))) } });
  };

  // 높이 리사이즈 핸들 — pointer 드래그.
  const onHandleDown = (e) => {
    if (!editable) return;
    e.preventDefault();
    const startY = e.clientY;
    const startH = height;
    const clamp = (h) => Math.max(MIN_H, Math.min(MAX_H, h));
    const move = (ev) => setDragH(clamp(startH + (ev.clientY - startY)));
    const up = (ev) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDragH(null);
      setHeight(clamp(startH + (ev.clientY - startY)));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  if (editable && (!url || editingUrl)) {
    return (
      <div className="bn-embed" contentEditable={false}>
        <div className="bn-url-form">
          <span className="bn-url-form-icon">🌐</span>
          <input
            className="bn-url-input"
            type="text"
            value={urlDraft}
            placeholder="임베드할 URL (YouTube · Vimeo · 지도 · PDF · 웹페이지)"
            spellCheck={false}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submitUrl(); }
              else if (e.key === 'Escape' && url) { e.preventDefault(); setUrlDraft(url); setEditingUrl(false); }
            }}
          />
          <button type="button" className="bn-url-submit" disabled={!normalizeUrl(urlDraft)} onClick={submitUrl}>
            임베드
          </button>
        </div>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="bn-embed" contentEditable={false}>
        <div className="bn-synced-note">비어 있는 임베드 블록</div>
      </div>
    );
  }

  const displayH = dragH != null ? dragH : height;
  // PDF 는 sandbox 생략 — Chromium 이 sandboxed iframe 내 내장 PDF 뷰어를 차단할 수 있음
  const pdf = isPdfUrl(src);
  // srcdoc 렌더 (자체 스토리지 HTML). fetch 실패 시 기존 iframe src 로 폴백.
  const useSrcdoc = srcdocMode && !srcdocFailed;

  return (
    <div className="bn-embed" contentEditable={false}>
      <div className="bn-embed-frame-wrap">
        {useSrcdoc && srcdocHtml == null ? (
          <div className="bn-embed-loading" style={{ height: displayH }}>HTML 불러오는 중…</div>
        ) : useSrcdoc ? (
          <iframe
            className="bn-embed-frame"
            srcDoc={srcdocHtml}
            title={`임베드: ${url}`}
            style={{ height: displayH }}
            // ⚠️ allow-same-origin 금지 — srcdoc 은 부모 origin 상속. opaque origin 으로 격리해
            // 첨부 HTML 스크립트가 앱 세션/스토리지에 접근하지 못하게 한다.
            sandbox="allow-scripts allow-popups allow-forms"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <iframe
            className="bn-embed-frame"
            src={src}
            title={`임베드: ${url}`}
            style={{ height: displayH }}
            sandbox={pdf ? undefined : 'allow-scripts allow-same-origin allow-popups allow-forms'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        )}
        {/* 드래그 중 iframe 이 pointermove 를 삼키지 않게 하는 투명 커버 */}
        {dragH != null ? <div className="bn-embed-drag-cover" /> : null}
      </div>
      {editable ? (
        <div className="bn-embed-toolbar">
          {PRESETS.map((p) => (
            <button
              key={p.h}
              type="button"
              className={`bn-embed-toolbar-btn${height === p.h ? ' is-active' : ''}`}
              onClick={() => setHeight(p.h)}
            >
              {p.label}
            </button>
          ))}
          <div
            className="bn-embed-handle"
            onPointerDown={onHandleDown}
            title="드래그해서 높이 조절"
            role="separator"
            aria-label="임베드 높이 조절"
          />
          <button
            type="button"
            className="bn-embed-toolbar-btn"
            onClick={() => { setUrlDraft(url); setEditingUrl(true); }}
          >
            주소 변경
          </button>
        </div>
      ) : null}
    </div>
  );
}

export const embedSpec = createReactBlockSpec(
  {
    type: 'embed',
    propSchema: {
      url: { default: '' },
      height: { default: 400 },
    },
    content: 'none',
  },
  {
    render: ({ block, editor }) => <EmbedView block={block} editor={editor} />,
  },
)();

export default embedSpec;
